/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useContext, useState } from "react";
import { PageContainer } from "@amzn/stencil-react-components/page";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { H1 } from "../../../../components/typography";
import { HVH_COLORS } from "../../../../config/palette";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { TAKE_BADGE_PHOTO_RESULT_URL, TAKE_BADGE_PHOTO_START_URL } from "../../../../config/urls";
import { useNavigator } from "../../../../hooks/use-navigator";
import { Context } from "../../../../reactContextStore/store";
import { BADGE_PHOTO_URL } from "../../../../config/urls";
import { getCandidateData } from "../../../../utility/candidate-data";
import { useMetrics } from "../../../../hooks/use-metrics";
import { useUrlParams } from "../../../../hooks/use-url-params";
import "./styles.scss";
import { extractMetricData } from "../../../../utility/badge-metric";
import { plainFetcher } from "../../../../helpers/fetcher";
import * as adobeAnalytics from "../../../../utility/adobe-analytics";
import { getNumberOfFailedAttempts } from "../../../../helpers/badge-failed-attempts";
import useArb from "../../../../hooks/use-arb";

export const BadgeConfirmRoute = () => {
  const [goToResults, goToBadgeStart] = useNavigator(TAKE_BADGE_PHOTO_RESULT_URL, TAKE_BADGE_PHOTO_START_URL);
  const { state } = useContext(Context);
  const { selectedShot } = state;
  const { applicationId } = useUrlParams();
  const [bannerDisplay, setBannerDisplay] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const bundle = useArb("HVHChecklistPantherResource");

  const { sendMetric } = useMetrics();
  const networkErrorMessage = bundle.getMessage("Checklist-BPConfirm-NetworkError");
  const submitErrorMessage = bundle.getMessage("Checklist-BPConfirm-GeneralError");

  const submit = useCallback(async () => {
    if (!window.navigator.onLine) {
      setErrorMessage(networkErrorMessage);
      setBannerDisplay(true);
      return;
    }
    setLoading(true);

    try {
      const { candidateId } = await getCandidateData(applicationId);

      const payload = {
        captureSource: "CANDIDATE",
        badgePhotoContents: selectedShot?.outputImageData ? selectedShot?.outputImageData.split(",").pop() : "",
      };
      await plainFetcher(BADGE_PHOTO_URL + "/" + candidateId, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      adobeAnalytics.addEventMetric(window, applicationId, "confirm badge", {
        referrer: adobeAnalytics.interpretReferrer(document.referrer),
      });
      sendMetric({
        event: "confirm-badge",
        details: {
          ...extractMetricData(selectedShot!),
          numberOfFailedAttempts: getNumberOfFailedAttempts(),
        },
      });
      goToResults();
    } catch (error) {
      console.error(error);
      setErrorMessage(submitErrorMessage);
      setBannerDisplay(true);
    }
    setLoading(false);
  }, [applicationId, selectedShot]);

  const dismissBanner = useCallback(() => {
    setErrorMessage("");
    setBannerDisplay(false);
  }, []);

  if (!selectedShot) {
    goToBadgeStart();
  }

  const confirm = bundle.getMessage("Checklist-BPConfirm-Confirm");
  const submitButton = bundle.getMessage("Checklist-General-SubmitButtonText");

  return (
    <>
      <PageContainer color={HVH_COLORS.NEUTRAL_90}>
        <Col gridGap="2rem" alignItems="stretch">
          {bannerDisplay && (
            <MessageBanner isDismissible={true} type={MessageBannerType.Error} onDismissed={dismissBanner}>
              {errorMessage}
            </MessageBanner>
          )}
          <Row justifyContent="center">
            <H1>{confirm}</H1>
          </Row>
          <Row justifyContent="center">
            <div className="confirm-image-container">
              <img className="preview-image limited-height" src={selectedShot?.outputImageData} alt="preview" />
            </div>
          </Row>
          <Row justifyContent="space-around" width="100%">
            <Button onClick={submit} variant={ButtonVariant.Primary} disabled={loading}>
              {submitButton}
            </Button>
          </Row>
        </Col>
      </PageContainer>
    </>
  );
};
